import { Typography } from '@mui/material';
import DocumentCitizenshipForm from 'civil-status/components/document-citizenship-form';
import { DocumentSearchCitizenshipCrumbs } from 'civil-status/constants/breadcrumb';
import createSchema from 'civil-status/utils/create-schema-citizenship';
import {
  transformCitizenshipDocument,
  transformCitizenshipRequest,
} from 'civil-status/utils/transform-document-citizenship';
import { DocumentCitizenshipFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { useFormik } from 'formik';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentCitizenshipCM } from 'shell/models/document-citizenship';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const typeAct = 'C';

export default function DocumentCitizenshipFormPage(): React.ReactElement {
  const params = useParams();

  const { userProfile } = useUserProfile();

  const [citizenship] = useHttpFetch<
    HttpResponse<DocumentCitizenshipCM>,
    DocumentCitizenshipFormVM
  >(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/visualizzazione-atto-cittadinanza
`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        params: {
          anno: params.anno,
          numeroRegistro: params.numeroRegistro,
          numeroAtto: params.numeroAtto,
          parte: params.parte,
          serie1: params.serie1,
          cognome: params.cognome,
          nome: params.nome,
        },
      },
      transformer: transformCitizenshipDocument,
    }
  );

  const updateCitizenship = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/modifica-atto-cittadinanza`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const substituteImage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/sostituzione-immagine-atto-cittadinanza`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const validationSchema = useMemo(() => {
    return createSchema(mode);
  }, [mode]);

  const formikForm = useFormik<DocumentCitizenshipFormVM>({
    initialValues: (citizenship.data as DocumentCitizenshipFormVM) || {
      matricola: '',
      surname: '',
      name: '',
      gender: '',
      municipalityCitizenship: '',
      surnameFather: '',
      nameFather: '',
      surnameMother: '',
      nameMother: '',
      time: null,
      birthday: null,
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      annotationNational: '',
      noteNational: '',
      annotationInternational: '',
      noteInternational: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values: DocumentCitizenshipFormVM, formContext) => {
      // If act substitution mode
      if (mode === PageMode.Substitution) {
        (
          substituteImage({
            data: {
              // TODO MAPPING, to test
              attoCittadinanzaForUpload: {
                numeroAtto: params.numeroAtto || undefined,
                numeroRegistro: values?.registerNumber || undefined,
                parte: params.parte || undefined,
                serie: params.serie1 || undefined,
                anno: params.anno || undefined,
                matricola: values.matricola || undefined,
                cognome: values.surname || undefined,
                nome: values.name || undefined,
                nomePadre: values.nameFather || undefined,
                filename: values.uploadFile?.file?.name || undefined,
                idOperatore: userProfile.datiLogin?.key?.userName || undefined,
              },
              file: values.uploadFile?.rawTiff?.split(',')[1] || undefined,
              mimeType: values.uploadFile?.file?.type || undefined,
              filename: values.uploadFile?.file?.name || undefined,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue(
            'indicatorUpdate',
            'È necessario effettuare la convalida dei dati.'
          );
        });
      }

      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformCitizenshipRequest(values);

        (
          updateCitizenship({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue('indicatorUpdate', '');
        });
      }
    },
  });

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
              cognome: params.cognome,
              nome: params.nome,
            },
          },
        });
      }
    };
  }, []);

  // visibility
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    setVisibility(!!formikForm.values?.indicatorUpdate);
  }, [formikForm.values?.indicatorUpdate]);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Breadcrumbs
        items={[
          ...DocumentSearchCitizenshipCrumbs,
          { name: 'Atto di Cittadinanza', path: '/stato-civile/cittadinanza/ricerca/dettaglio' },
        ]}
        base="stato-civile"
      />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Atto di Cittadinanza {mode === PageMode.International ? '- Annotazioni Internazionali' : ''}
        {mode === PageMode.National ? '- Annotazioni Italia' : ''}
      </Typography>
      {citizenship.isLoading || citizenship.pristine || citizenship.hasErrors ? undefined : (
        <form onSubmit={formikForm.handleSubmit}>
          <SearchMessages
            type={MessageType.Warning}
            title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
            messages={
              formikForm.values?.indicatorUpdate
                ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
                : []
            }
            visibility={visibility}
            onChangeVisibility={() => setVisibility(false)}
          />
          <DocumentCitizenshipForm
            onChanges={onChanges}
            onChange={formikForm.setFieldValue}
            values={formikForm.values}
            errors={formikForm.errors}
            touched={formikForm.touched}
            mode={mode}
            onEditMode={() => {
              setMode(PageMode.Edit);
            }}
            onSubstitutionMode={() => {
              setMode(PageMode.Substitution);
            }}
            onDetailMode={() => {
              setMode(PageMode.Detail);
            }}
            onPrintMode={() => {
              setMode(PageMode.Print);
            }}
            onAnnotationInternationalMode={() => {
              setMode(PageMode.International);
            }}
            onAnnotationNationalMode={() => {
              setMode(PageMode.National);
            }}
            type={typeAct}
          />
        </form>
      )}
    </>
  );
}

DocumentCitizenshipFormPage.displayName = 'home page component';
